import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { useMe } from "@/api/households";
import { Layout } from "@/components/Layout";
import { Loading } from "@/components/ui";
import { AccountsPage } from "@/pages/AccountsPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { ImportPage } from "@/pages/ImportPage";
import { CallbackPage } from "@/pages/CallbackPage";
import { CategoriesPage } from "@/pages/CategoriesPage";
import { LoginPage } from "@/pages/LoginPage";
import { NewAccountPage } from "@/pages/NewAccountPage";
import { NewTransactionPage } from "@/pages/NewTransactionPage";
import { NewTransferPage } from "@/pages/NewTransferPage";
import { BudgetsPage } from "@/pages/BudgetsPage";
import { ReportPage } from "@/pages/ReportPage";
import { UpcomingPage } from "@/pages/UpcomingPage";
import { GoalsPage } from "@/pages/GoalsPage";
import { DataPage } from "@/pages/DataPage";
import { HistoryPage } from "@/pages/HistoryPage";
import { ManualPage } from "@/pages/ManualPage";
import { OnboardingPage } from "@/pages/OnboardingPage";
import { TransactionsPage } from "@/pages/TransactionsPage";

/**
 * Sends the caller wherever they should be after signing in.
 *
 * One request answers it: `me` carries the households the caller has joined, so
 * "first household" and "which one" are the same lookup, and the app never
 * renders a shell it then has to replace.
 */
function Home() {
  const { isAuthenticated } = useAuth();
  const { data, isLoading } = useMe(isAuthenticated);

  if (isLoading || !data) return <Loading />;
  const first = data.households[0];
  if (!first) return <Navigate to="/households/new" replace />;
  // The dashboard, not the account list: project.md §5.3 says the product
  // opens on the answer to one question, and a list of balances is not it.
  return <Navigate to={`/households/${first.id}/dashboard`} replace />;
}

/**
 * Guards a household route.
 *
 * A household the caller is not a member of is not rendered at all — the API
 * would answer 404 (K-08), and the SPA should not paint a page that is about to
 * be empty. Redirecting to the caller's own household is the same answer without
 * the flicker.
 */
function RequireHousehold({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { householdId } = useParams();
  const { data, isLoading } = useMe(isAuthenticated);

  if (isLoading || !data) return <Loading />;
  if (!data.households.some((h) => h.id === householdId)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/** Everything below the sign-in wall. */
function SignedIn() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/households/new" element={<OnboardingPage />} />
      <Route
        path="/households/:householdId/*"
        element={
          <RequireHousehold>
            <Routes>
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="accounts" element={<AccountsPage />} />
              <Route path="accounts/new" element={<NewAccountPage />} />
              <Route path="transactions" element={<TransactionsPage />} />
              <Route path="transactions/new" element={<NewTransactionPage />} />
              <Route path="transfers/new" element={<NewTransferPage />} />
              <Route path="budgets" element={<BudgetsPage />} />
              <Route path="reports" element={<ReportPage />} />
              <Route path="upcoming" element={<UpcomingPage />} />
              <Route path="goals" element={<GoalsPage />} />
              <Route path="import" element={<ImportPage />} />
              <Route path="categories" element={<CategoriesPage />} />
              {/*
                Reached from the overview rather than from the tab bar. Eight
                tabs is what K-15's speed gate has already been measured
                against, and "who changed this" is a question somebody asks
                while looking at a number rather than one they navigate to.
              */}
              <Route path="history" element={<HistoryPage />} />
              <Route path="data" element={<DataPage />} />
              <Route path="manual/*" element={<ManualPage />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </RequireHousehold>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  const { isAuthenticated, loading } = useAuth();

  return (
    <BrowserRouter>
      <Routes>
        {/*
          The callback sits outside the layout and outside the sign-in check: it
          runs *while* signing in, and wrapping it in a guard that asks whether
          the caller is signed in yet would bounce them off the page that is
          about to sign them in.
        */}
        <Route path="/auth/callback" element={<CallbackPage />} />
        <Route
          path="*"
          element={
            <Layout>
              {loading ? <Loading /> : isAuthenticated ? <SignedIn /> : <LoginPage />}
            </Layout>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
